import { useState } from "react";
import { FileText, Upload, Loader2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * Feb-2026 (User-Wunsch) — Serviceheft / Verkaufsrechnung / weitere Dokumente.
 * In die „Service & Wartung"-Card verschoben (links neben „Beschreibung &
 * Service-Hinweise"). Belege werden als kind="service_doc" in `document_urls`
 * archiviert und beim Kauf an den Käufer ausgehändigt. Zusätzlich kann die KI
 * die hochgeladenen Dokumente auswerten und einen Beschreibungsvorschlag für
 * „Beschreibung & Service-Hinweise" (form.description) erzeugen.
 */
export default function ServiceDocsUpload({ form, update }) {
    const [uploadingDocs, setUploadingDocs] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);

    const handleServiceDocUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        setUploadingDocs(true);
        try {
            const uploaded = [];
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/upload/image", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                uploaded.push({ url: data.url, name: f.name, kind: "service_doc" });
            }
            const merged = [...(form.document_urls || []), ...uploaded];
            update("document_urls", merged);
            toast.success(`${uploaded.length} Service-Beleg(e) hochgeladen`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
        } finally {
            setUploadingDocs(false);
        }
    };

    const removeServiceDoc = (idx) => {
        const list = [...(form.document_urls || [])];
        list.splice(idx, 1);
        update("document_urls", list);
    };

    const serviceDocs = (form.document_urls || []).filter(
        (d) => (d?.kind || "").toLowerCase() === "service_doc",
    );

    // Feb-2026 (User-Wunsch) — KI wertet die hochgeladenen Service-Dokumente
    // (Serviceheft / Rechnungen / Inspektionsnachweise) aus und schlägt einen
    // Beschreibungstext für „Beschreibung & Service-Hinweise" (form.description) vor.
    const analyzeDocsWithAi = async () => {
        const docUrls = serviceDocs.map((d) => d.url).filter(Boolean);
        if (!docUrls.length) {
            toast.info("Bitte zuerst Dokumente hochladen.");
            return;
        }
        setAiLoading(true);
        try {
            const { data } = await api.post("/listings/ai-service-docs-analyze", {
                doc_urls: docUrls,
                brand: form.brand,
                model: form.model,
                year: form.first_registration_date
                    ? parseInt(String(form.first_registration_date).slice(0, 4), 10)
                    : form.year,
                mileage: Number(form.mileage) || 0,
            });
            const suggestion = (data?.suggestion || "").trim();
            if (!suggestion) {
                toast.warning(
                    data?.mocked
                        ? "KI nicht erreichbar — bitte später erneut versuchen."
                        : "Aus den Dokumenten konnte kein Text erstellt werden.",
                );
                return;
            }
            const existing = (form.description || "").trim();
            const merged = existing ? `${existing}\n\n${suggestion}` : suggestion;
            update("description", merged.slice(0, 4000));
            toast.success("KI-Beschreibung aus Dokumenten in „Beschreibung & Service-Hinweise“ übernommen.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "KI-Auswertung fehlgeschlagen.");
        } finally {
            setAiLoading(false);
        }
    };

    return (
        <div className="border border-gray-200 rounded-sm p-4" data-testid="ad-reach-service-docs-block">
            <div className="font-bold text-[#111827] flex items-center gap-1.5 mb-1">
                <FileText className="w-4 h-4 text-gray-700" />
                Serviceheft / Verkaufsrechnung / weitere Dokumente
            </div>
            <p className="text-[11px] text-gray-500 mb-2">
                Belege hochladen — werden bei diesem Inserat archiviert und beim Kauf an den Käufer ausgehändigt. PDF oder Bilder bis 10 MB.
            </p>
            <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-bold uppercase tracking-wider bg-white border border-gray-300 hover:border-[#111827] rounded-sm px-3 py-2" data-testid="ad-reach-service-docs-upload-label">
                    {uploadingDocs ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploadingDocs ? "Lade hoch…" : "Dokumente wählen"}
                    <input
                        type="file"
                        accept="image/*,application/pdf"
                        multiple
                        onChange={handleServiceDocUpload}
                        className="hidden"
                        data-testid="ad-reach-service-docs-upload-input"
                    />
                </label>
                {serviceDocs.length > 0 && (
                    <button
                        type="button"
                        onClick={analyzeDocsWithAi}
                        disabled={aiLoading}
                        className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#111827] hover:bg-black text-[#FFCC00] rounded-sm px-3 py-2 disabled:opacity-50"
                        data-testid="ad-reach-service-docs-ai-btn"
                        title="KI liest die hochgeladenen Dokumente und erstellt einen Beschreibungsvorschlag für „Beschreibung & Service-Hinweise“."
                    >
                        {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                        {aiLoading ? "Werte aus…" : "KI: Beschreibung aus Dokumenten"}
                    </button>
                )}
            </div>
            {serviceDocs.length > 0 && (
                <ul className="mt-3 space-y-1.5" data-testid="ad-reach-service-docs-list">
                    {serviceDocs.map((doc) => {
                        const fullIdx = (form.document_urls || []).indexOf(doc);
                        return (
                            <li key={doc.url} className="flex items-center gap-2 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-xs">
                                <FileText className="w-4 h-4 text-gray-500 shrink-0" />
                                <a href={doc.url} target="_blank" rel="noreferrer" className="truncate flex-1 hover:underline">
                                    {doc.name || "Beleg"}
                                </a>
                                <button
                                    type="button"
                                    onClick={() => removeServiceDoc(fullIdx)}
                                    className="p-1 text-gray-400 hover:text-red-600"
                                    data-testid={`ad-reach-service-doc-remove-${fullIdx}`}
                                    aria-label="Entfernen"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
